import type { PillTone } from '@/components/StatusPill';
import type {
  ComparabilityVerdict,
  ComparedResult,
  ComparisonType,
  ControlState,
  ComparisonSide,
  ScenarioChange,
} from '@/api/validationRuns';

// ─────────────────────────────────────────────────────────────────────────────
// The words and order of a run comparison (EVAL-06 STUDIO-EVAL-06, extended by SI-07). ONE home: the Compare drawer and
// the Compare page both read these, so "what got worse comes first" and every label say the same thing everywhere.
// The server decides every change, verdict and sentence (ValidationRunComparisonRules); nothing here judges.
// ─────────────────────────────────────────────────────────────────────────────

export const CHANGE: Record<ScenarioChange, { label: string; tone: PillTone; explain: string }> = {
  NewlyFailing: { label: 'Newly failing', tone: 'red', explain: 'Passed before, fails now.' },
  Resolved: { label: 'Fixed', tone: 'green', explain: 'Failed before, passes now.' },
  StillFailing: { label: 'Still failing', tone: 'amber', explain: 'Fails in both runs.' },
  Inconclusive: { label: 'Inconclusive', tone: 'slate', explain: 'A conversation did not complete, so its checks could not all run.' },
  NotComparable: { label: 'Not comparable', tone: 'violet', explain: 'The scenario differs between the runs, so the results test different things. Shown apart, never scored.' },
  MissingFromCandidate: { label: 'Not run this time', tone: 'slate', explain: 'Ran before, not in this run. Listed so nothing disappears from the totals.' },
  NewInCandidate: { label: 'New this time', tone: 'ink', explain: 'Ran in this run only. Listed, not scored.' },
  StillPassing: { label: 'Still passing', tone: 'green', explain: 'Passes in both runs, every repeat.' },
};

/** Regressions first, then improvements, then what is still wrong, then what cannot be compared, then what passed both times. */
export const ORDER: ScenarioChange[] = [
  'NewlyFailing', 'Resolved', 'StillFailing', 'Inconclusive', 'NotComparable', 'MissingFromCandidate', 'NewInCandidate', 'StillPassing',
];

export const TYPE_LABEL: Record<ComparisonType, string> = {
  AgentChange: 'Agent change',
  ModelChange: 'Model change',
  CombinedChange: 'Combined change',
  EvaluatorChange: 'Evaluator change',
};

export const TYPE_ORDER: ComparisonType[] = ['AgentChange', 'ModelChange', 'CombinedChange', 'EvaluatorChange'];

/** The banner's antd Alert type per verdict: green only when the pair is a fair test of the one difference. */
export const VERDICT_ALERT: Record<ComparabilityVerdict, 'success' | 'warning' | 'info' | 'error'> = {
  Comparable: 'success',
  Confounded: 'warning',
  NotComparable: 'warning',
  Unavailable: 'info',
};

export const CONTROL_STATE: Record<ControlState, { label: string; tone: PillTone }> = {
  Held: { label: 'Held the same', tone: 'green' },
  Changed: { label: 'Differs', tone: 'amber' },
  NotRecorded: { label: 'Not recorded', tone: 'slate' },
  NotYetRecorded: { label: 'Not recorded yet', tone: 'slate' },
};

/** A single-result side as the old drawer worded it; a side with repeats uses the server's summary ("1 of 3 passed"). */
export function outcomeLabel(r: ComparedResult | null | undefined): { text: string; tone: PillTone } {
  if (!r) return { text: 'Not run', tone: 'slate' };
  const many = (r.trials?.length ?? 1) > 1;
  if (!r.completed) return { text: many && r.summary ? r.summary : 'Did not complete', tone: 'slate' };
  if (r.passed) return { text: many && r.summary ? r.summary : 'Passed', tone: 'green' };
  return { text: many && r.summary ? r.summary : 'Failed', tone: 'red' };
}

/** "folfox-patient · Live · gpt-5.4-1" — the column label: Agent · Candidate revision · Actual deployment, never a display name. */
export function identityLabel(id: ComparisonSide | null | undefined, fallbackRunId?: number): string {
  if (!id) return fallbackRunId != null ? `Run #${fallbackRunId}` : 'Run';
  const deployment = id.deployments.length > 0
    ? id.deployments.map((d) => d.deployment ?? 'not named').join(' + ')
    : id.configuredDeployment ?? 'deployment not recorded';
  return [id.agentName ?? `Agent #${id.agentId}`, shortRevision(id), deployment].join(' · ');
}

/** "Live", "Earlier revision", "Candidate version, not live" — with the short hash where one was recorded. */
export function shortRevision(id: ComparisonSide): string {
  if (!id.candidateHash) return 'revision not recorded';
  return `${id.revision} · hash ${id.candidateHash.slice(0, 7)}`;
}
